/**
 * @file Topbar.tsx
 * @brief Top navigation — flush GridBlock chrome aligned to page rail.
 *
 * @details
 * Hairline cells (logo, scope menu, page title, search, theme, profile)
 * share the same unpadded ~1280px rail as page GridBlocks.
 * Flex row of `GridCell`s (not CSS grid) so breakpoint `hidden` cells don't
 * break column templates. Mega menu: flush GridBlock on same rail.
 *
 * @author DES
 * @date 26-05-2026
 * @updated 29-07-2026 — GridBlock header chrome; translucent bar like footer
 * @updated 30-09-2026 — quick highlights moved to their own row below the header
 * @updated 30-09-2026 — DES: the quick-nav row is gone; Home sits in the
 *   header between the logo and the scope menu, which is now the only menu.
 * @updated 12-10-2026 — DES: Inbox after Home (not for a superadmin); Home is
 *   the filled house (HouseFilled), as on the phone.
 * @updated 13-10-2026 — DES: a superadmin gets Home + Inbox like everyone.
 */

import {
  ChevronDown,
  LayoutGrid,
  LogOut,
  Menu,
  Moon,
  Search,
  Sun,
  UserCog,
  CreditCard,
  Gauge,
  Sparkles,
} from "lucide-react";
import { useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Avatar } from "@/components/ui/Avatar";
import { BrandIcon } from "@/components/brand/BrandIcon";
import { HouseFilled } from "@/components/icons/HouseFilled";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { GridBlock, GridCell } from "@/features/analytics/components/GridBlock";
import { useAuth } from "@/shared/auth/auth-context";
import { isCompanyAdminRole } from "@/shared/auth/homePath";
import { billingAudienceFor } from "@/features/my-billing/billingErrors";
import { useTheme } from "@/shared/theme/theme-context";
import { cn } from "@/shared/theme/cn";
import { getInitials } from "@/shared/utils/formatters";
import { InboxTopbarButton } from "@/features/inbox/InboxTopbarButton";
import { NavMegaMenu } from "./NavMegaMenu";
import { resolveNavShellLabel } from "./nav-utils";
import { shellRailMaxClass } from "./shell-rail";
import type { NavShellConfig } from "./nav-types";
import { PlanPill } from "@/shared/billing/PlanPill";
import { plansHref } from "@/shared/billing/planSummary";
import { usePlanSummary } from "@/shared/billing/usePlanSummary";

const flatChromeBtn =
  "inline-flex h-full w-full items-center gap-1.5 rounded-none border-0 bg-transparent px-3 text-sm font-medium transition-colors hover:bg-muted/40 focus:outline-none focus-visible:bg-muted/50";

interface TopbarProps {
  nav: NavShellConfig;
  onOpenCommandPalette?: () => void;
}

export function Topbar({ nav, onOpenCommandPalette }: TopbarProps) {
  const { user, logout } = useAuth();
  const { resolvedTheme, setTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const initials = getInitials(user?.firstName, user?.lastName, user?.email);
  const displayName =
    user?.name ||
    [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
    user?.email ||
    "User";

  // By role: a plain user's profile lives in /app. It used to fall through to
  // /company/account, which bounced them to Forbidden (30-09-2026).
  const profileHref =
    user?.role === "superadmin"
      ? "/admin/account"
      : isCompanyAdminRole(user?.role)
        ? "/company/account"
        : "/app/account";

  // Billing in the user menu (17-10-2026): a personal user's own, a company
  // admin's company page. A company member's company pays, so none.
  const audience = billingAudienceFor(user);
  const billingHref =
    user?.role === "superadmin"
      ? null
      : audience === "company_admin"
        ? "/company/billing"
        : audience === "personal"
          ? "/app/billing/cards"
          : null;
  // The plan pill and "Plans" (21-10-2026): personal users and company
  // admins; a member sees the company's plan name and no link.
  const { plan } = usePlanSummary();
  const plansLink = plansHref(plan);
  // Usage (DES 21-10-2026): personal users only (company admins read minutes
  // on their plan card; a member's company pays), with minutes left beside it.
  const usageHref = audience === "personal" && user?.role !== "superadmin" ? "/app/billing/usage" : null;
  const minutesLeft =
    plan?.intelIncluded != null && plan.intelIncluded > 0
      ? Math.max(0, plan.intelIncluded - (plan.intelUsed ?? 0))
      : null;

  const activeLabel = useMemo(
    () => resolveNavShellLabel(nav, location.pathname, location.hash),
    [nav, location.pathname, location.hash],
  );

  const pageTitle = activeLabel ?? nav.scopeSubtitle ?? nav.scopeTitle;

  return (
    <>
      <header className="border-b border-border/60 bg-app-page/90 backdrop-blur-md supports-[backdrop-filter]:bg-app-page/75">
        <div className={shellRailMaxClass}>
          <GridBlock className="bg-transparent">
            <div className="flex h-14 min-w-0">
              <GridCell padded={false} className="flex shrink-0">
                <button
                  type="button"
                  onClick={() => navigate(nav.homeTo)}
                  className={cn(flatChromeBtn, "justify-center px-4 md:px-5")}
                  aria-label="Go to dashboard"
                >
                  <BrandIcon size={26} />
                </button>
              </GridCell>

              {/* Home, before the scope menu (DES 30-09-2026). */}
              <GridCell padded={false} className="flex shrink-0">
                <NavLink
                  to={nav.homeTo}
                  end
                  aria-label="Home"
                  title="Home"
                  className={({ isActive }) =>
                    cn(
                      flatChromeBtn,
                      "justify-center px-4",
                      isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground",
                    )
                  }
                >
                  <HouseFilled className="h-5 w-5" />
                </NavLink>
              </GridCell>

              {/* Inbox after Home (DES 12-10-2026, the phone header's Home ·
                  Inbox · Menu); a superadmin too since 13-10-2026. */}
              <GridCell padded={false} className="flex shrink-0">
                <InboxTopbarButton className={cn(flatChromeBtn, "justify-center px-4")} />
              </GridCell>

              <GridCell padded={false} className="flex shrink-0">
                <button
                  type="button"
                  onClick={() => setMenuOpen(true)}
                  className={cn(
                    flatChromeBtn,
                    "min-w-0 max-w-[140px] sm:max-w-[180px]",
                    menuOpen && "bg-brand/5 text-foreground",
                  )}
                  aria-expanded={menuOpen}
                  aria-haspopup="dialog"
                >
                  <LayoutGrid className="hidden h-4 w-4 shrink-0 text-muted-foreground sm:block" />
                  <Menu className="h-4 w-4 shrink-0 text-muted-foreground sm:hidden" />
                  <span className="truncate">{nav.scopeTitle}</span>
                  <ChevronDown
                    className={cn(
                      "h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-300 ease-out",
                      menuOpen && "rotate-180",
                    )}
                  />
                </button>
              </GridCell>

              <GridCell
                padded={false}
                className="hidden min-w-0 flex-1 items-center px-4 md:flex"
              >
                <span className="truncate text-lg font-semibold tracking-tight text-muted-foreground">
                  {pageTitle}
                </span>
              </GridCell>

              {onOpenCommandPalette ? (
                <GridCell padded={false} className="hidden shrink-0 sm:flex">
                  <button
                    type="button"
                    className={cn(flatChromeBtn, "gap-2 text-muted-foreground")}
                    aria-label="Open command palette"
                    onClick={onOpenCommandPalette}
                  >
                    <Search className="h-3.5 w-3.5" />
                    <span className="hidden xl:inline">Search</span>
                    <kbd className="pointer-events-none border border-border/60 bg-muted/40 px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                      ⌘K
                    </kbd>
                  </button>
                </GridCell>
              ) : null}

              <GridCell padded={false} className="flex shrink-0 sm:hidden">
                {onOpenCommandPalette ? (
                  <button
                    type="button"
                    className={cn(flatChromeBtn, "justify-center px-3")}
                    aria-label="Open command palette"
                    onClick={onOpenCommandPalette}
                  >
                    <Search className="h-4 w-4" />
                  </button>
                ) : (
                  <button
                    type="button"
                    className={cn(flatChromeBtn, "justify-center px-3")}
                    aria-label="Open navigation menu"
                    onClick={() => setMenuOpen(true)}
                  >
                    <Menu className="h-4 w-4" />
                  </button>
                )}
              </GridCell>

              <GridCell padded={false} className="flex shrink-0">
                <button
                  type="button"
                  className={cn(flatChromeBtn, "justify-center px-3")}
                  aria-label="Toggle theme"
                  onClick={() =>
                    setTheme(resolvedTheme === "dark" ? "light" : "dark")
                  }
                >
                  {resolvedTheme === "dark" ? (
                    <Sun className="h-4 w-4" />
                  ) : (
                    <Moon className="h-4 w-4" />
                  )}
                </button>
              </GridCell>

              <GridCell padded={false} className="flex shrink-0">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      aria-label="Open profile menu"
                      className={cn(flatChromeBtn, "gap-2 px-3 md:px-4")}
                    >
                      <Avatar
                        src={user?.profileImageUrl}
                        initials={initials}
                        size="sm"
                        tone="brand"
                        alt={displayName}
                      />
                      <span className="hidden flex-col leading-tight sm:flex">
                        <span className="max-w-[140px] truncate text-left text-xs font-medium">
                          {displayName}
                        </span>
                        <span className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                          {user?.role ?? "user"}
                        </span>
                      </span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel className="flex flex-col gap-0.5">
                      <span className="flex min-w-0 items-center gap-2">
                        <span className="truncate text-sm font-semibold">
                          {displayName}
                        </span>
                        {/* Which plan (21-10-2026); a member sees the company's. */}
                        {plan ? <PlanPill plan={plan} className="shrink-0" /> : null}
                      </span>
                      <span className="truncate text-[11px] font-normal text-muted-foreground">
                        {user?.email}
                      </span>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      /* ⛔ No preventDefault here (04-10-2026): it kept this
                         menu open under the Profile palette, and an open Radix
                         menu sets `pointer-events: none` on the page, so the
                         palette's ✕ and backdrop could not be clicked. */
                      onSelect={() => navigate(profileHref)}
                    >
                      <UserCog className="mr-2 h-4 w-4" />
                      Edit profile
                    </DropdownMenuItem>
                    {plansLink ? (
                      <DropdownMenuItem onSelect={() => navigate(plansLink)}>
                        <Sparkles className="mr-2 h-4 w-4" />
                        Plans
                      </DropdownMenuItem>
                    ) : null}
                    {usageHref ? (
                      <DropdownMenuItem onSelect={() => navigate(usageHref)}>
                        <Gauge className="mr-2 h-4 w-4" />
                        Usage
                        {minutesLeft != null ? (
                          <span className="ml-auto pl-3 text-xs tabular-nums text-muted-foreground">
                            {minutesLeft.toLocaleString("en-GB")} min left
                          </span>
                        ) : null}
                      </DropdownMenuItem>
                    ) : null}
                    {billingHref && billingHref !== plansLink ? (
                      <DropdownMenuItem onSelect={() => navigate(billingHref)}>
                        <CreditCard className="mr-2 h-4 w-4" />
                        Billing
                      </DropdownMenuItem>
                    ) : null}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onSelect={(e) => {
                        e.preventDefault();
                        void logout();
                      }}
                      className="text-destructive focus:text-destructive"
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      Sign out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </GridCell>
            </div>
          </GridBlock>
        </div>

      </header>

      <NavMegaMenu config={nav} open={menuOpen} onOpenChange={setMenuOpen} />
    </>
  );
}
